import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar from '../../components/navigation/Sidebar';
import NotificationCenter from '../../components/navigation/NotificationCenter';
import QuickActionMenu from '../../components/navigation/QuickActionMenu';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import toast from 'react-hot-toast';

const confidenceBadge = (score) => {
  if (score === null || score === undefined) return null;
  const pct = Math.round(score * 100);
  const color =
    pct >= 80 ? 'text-success bg-success/10' :
    pct >= 60 ? 'text-warning bg-warning/10' :
                'text-error bg-error/10';
  return { pct, color };
};

const ReportsList = () => {
  const navigate = useNavigate();
  const { user, userProfile, signOut } = useAuth();
  const isAdmin = userProfile?.role === 'admin';
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (user) fetchReports();
  }, [user]);

  const fetchReports = async () => {
    try {
      setIsLoading(true);
      let query = supabase
        .from('cases')
        .select(`
          id,
          case_number,
          persona,
          incident_date,
          property_address,
          property_type,
          allegation_type,
          claim_number,
          estimated_loss,
          triage_confidence,
          triage_summary,
          report_generated_at,
          created_at,
          reports (
            id,
            generated_at,
            pdf_url
          )
        `)
        .eq('status', 'report_ready')
        .order('report_generated_at', { ascending: false });

      if (!isAdmin) {
        query = query.eq('user_id', user.id);
      }

      const { data, error } = await query;

      if (error) throw error;
      setReports(data || []);
    } catch (err) {
      console.error('Fetch reports error:', err);
      toast.error('Failed to load reports');
    } finally {
      setIsLoading(false);
    }
  };

  const personaLabels = {
    'insurance-adjuster': 'Insurance Adjuster',
    'plaintiff-attorney': 'Plaintiff Attorney',
    'defense-attorney': 'Defense Attorney',
    'siu-investigator': 'SIU Investigator',
    'subrogation-specialist': 'Subrogation Specialist',
    'prosecutor': 'Prosecutor',
    'fire-marshal': 'Fire Marshal / AHJ',
    'risk-manager': 'Risk Manager',
    'property-owner': 'Property Owner',
  };

  const allegationLabels = {
    'arson': 'Arson',
    'electrical-fire': 'Electrical Fire',
    'suspicious-fire': 'Suspicious Fire',
    'accidental-fire': 'Accidental Fire',
    'wildfire': 'Wildfire',
    'explosion': 'Explosion',
  };

  const filtered = reports.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.case_number?.toLowerCase().includes(q) ||
      r.property_address?.toLowerCase().includes(q) ||
      r.allegation_type?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-background">
      <Sidebar userRole={userProfile?.role || 'client'} />
      <QuickActionMenu userRole={userProfile?.role || 'client'} />

      <div className="lg:ml-60 min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-card border-b border-border px-4 md:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-foreground">{isAdmin ? 'All Cases' : 'My Reports'}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {isLoading ? 'Loading…' : `${reports.length} completed report${reports.length !== 1 ? 's' : ''}`}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <NotificationCenter userRole={userProfile?.role || 'client'} />
              {!isAdmin && (
                <Button
                  variant="default"
                  iconName="FilePlus"
                  iconPosition="left"
                  onClick={() => navigate('/new-case-creation-wizard')}
                  className="hidden md:flex"
                >
                  New Case
                </Button>
              )}
              <Button
                variant="outline"
                iconName="LogOut"
                iconPosition="left"
                onClick={async () => { await signOut(); navigate('/login'); }}
              >
                Logout
              </Button>
            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6 md:py-8">

          {/* Search */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search by case number, address or type…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground placeholder:text-muted-foreground"
              />
            </div>
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-24">
              <div className="text-center">
                <Icon name="Loader" size={40} className="animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground text-sm">Loading your reports…</p>
              </div>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <Icon name="FileText" size={28} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {search ? 'No reports match your search' : 'No completed reports yet'}
              </h3>
              <p className="text-sm text-muted-foreground max-w-sm mb-6">
                {search
                  ? 'Try a different search term.'
                  : 'Once a case is analysed and a report is generated, it will appear here.'}
              </p>
              {!search && (
                <Button
                  variant="default"
                  iconName="FilePlus"
                  iconPosition="left"
                  onClick={() => navigate('/new-case-creation-wizard')}
                >
                  Create Your First Case
                </Button>
              )}
            </div>
          )}

          {/* Report cards */}
          {!isLoading && filtered.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {filtered.map((item) => {
                const report = item.reports?.[0];
                const badge = confidenceBadge(item.triage_confidence);
                const generatedDate = report?.generated_at || item.report_generated_at;

                return (
                  <div
                    key={item.id}
                    className="bg-card border border-border rounded-lg p-5 flex flex-col gap-4 hover:shadow-elevation-md transition-smooth group"
                  >
                    {/* Top row */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon name="FileText" size={18} className="text-primary" />
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground">Case</p>
                          <p className="text-sm font-semibold text-foreground">#{item.case_number}</p>
                        </div>
                      </div>

                      {badge && (
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${badge.color} flex-shrink-0`}>
                          {badge.pct}% confidence
                        </span>
                      )}
                    </div>

                    {/* Address */}
                    <div>
                      <p className="text-sm font-medium text-foreground line-clamp-1">{item.property_address}</p>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                        <span className="text-xs text-muted-foreground capitalize">
                          {allegationLabels[item.allegation_type] || item.allegation_type}
                        </span>
                        <span className="text-xs text-muted-foreground">·</span>
                        <span className="text-xs text-muted-foreground capitalize">
                          {item.property_type}
                        </span>
                      </div>
                    </div>

                    {/* Meta */}
                    <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <Icon name="Calendar" size={13} />
                        <span>
                          Incident: {item.incident_date
                            ? new Date(item.incident_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : '—'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Icon name="CheckCircle" size={13} className="text-success" />
                        <span className="text-success">
                          {generatedDate
                            ? new Date(generatedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : 'Report ready'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 col-span-2">
                        <Icon name="User" size={13} />
                        <span>{personaLabels[item.persona] || item.persona}</span>
                      </div>
                      {item.claim_number && (
                        <div className="flex items-center gap-1.5 col-span-2">
                          <Icon name="Hash" size={13} />
                          <span>Claim {item.claim_number}</span>
                        </div>
                      )}
                    </div>

                    {/* Triage summary */}
                    {item.triage_summary && (
                      <p className="text-xs text-muted-foreground line-clamp-2 border-t border-border pt-3">
                        {item.triage_summary}
                      </p>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-auto pt-2 border-t border-border">
                      <Button
                        variant="default"
                        size="sm"
                        iconName="ExternalLink"
                        iconPosition="left"
                        className="flex-1"
                        onClick={() => navigate(`/report-viewer/${item.id}`)}
                      >
                        View Report
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="ArrowRight"
                        iconPosition="left"
                        onClick={() => navigate(`/client-dashboard`)}
                        title="Back to dashboard"
                      >
                        Dashboard
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        </main>
      </div>
    </div>
  );
};

export default ReportsList;
